export type SupportTicketSource = 'omicares' | 'care4giver';

export type SupportTicketStatus = 'open' | 'in_progress' | 'resolved';

/** Language for user-facing support emails (reporter’s app locale). */
export type SupportTicketReporterLocale = 'en' | 'fr';

/** Normalize API/client locale values to en | fr. */
export function normalizeSupportTicketReporterLocale(
  v: unknown
): SupportTicketReporterLocale {
  if (v === 'fr' || v === 'FR') {
    return 'fr';
  }
  if (typeof v === 'string' && v.toLowerCase().startsWith('fr')) {
    return 'fr';
  }
  return 'en';
}

/** Who wrote a follow-up message on a ticket (stored on each message). */
export type SupportTicketMessageAuthor = 'user' | 'admin';

export interface SupportTicketMessageModel {
  _id: string;
  ticketId: string;
  author: SupportTicketMessageAuthor;
  /** When support asks the reporter for more information (admin only). */
  isClarificationRequest: boolean;
  body: string;
  authorUserId: string;
  createdAt: Date;
}

export interface SupportTicketAddMessageDto {
  body: string;
  /** Ignored for reporters; admins may set to label the email as a clarification request. */
  isClarificationRequest?: boolean;
  /** When the reporter sends a message, updates stored locale for future emails. */
  reporterLocale?: SupportTicketReporterLocale;
}

export interface SupportTicketMessagesResponse {
  items: SupportTicketMessageModel[];
}

export interface SupportTicketModel {
  _id: string;
  source: SupportTicketSource;
  subject: string;
  description: string;
  status: SupportTicketStatus;
  reporterUserId: string;
  reporterEmail: string;
  /** Reporter UI locale for end-user emails (defaults to en if missing). */
  reporterLocale?: SupportTicketReporterLocale;
  createdAt: Date;
  updatedAt: Date;
  /** When the reporter last opened the ticket thread (server). */
  reporterLastReadAt?: Date;
  /** When support last opened the ticket thread (any admin; server). */
  supportLastReadAt?: Date;
  /** Set only on list API responses: messages from the other party since last read. */
  unreadMessageCount?: number;
}

export interface SupportTicketCreateDto {
  subject: string;
  description: string;
  source: SupportTicketSource;
  reporterLocale?: SupportTicketReporterLocale;
}

export interface SupportTicketListQuery {
  status?: SupportTicketStatus;
  source?: SupportTicketSource;
  page?: number;
  limit?: number;
}

export interface SupportTicketAdminListResponse {
  items: SupportTicketModel[];
  total: number;
  page: number;
  limit: number;
}
